import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Input,
  LinearProgress,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AttachFile, Send } from '@mui/icons-material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { format } from 'date-fns';
import 'react-perfect-scrollbar/dist/css/styles.css';

import { useAuthContext } from '../../context/AuthContext';
import { useWSContext } from '../../context/WebSocketContext';
import serverArquivo from '../../services/serverArquivo';

const isWsTruthy = (value) =>
  value === true || value === 'True' || value === 'true' || value === 1 || value === '1';

function AudioReplyInfo({ url }) {
  const [durationText, setDurationText] = useState('--:--');

  useEffect(() => {
    if (!url) {
      setDurationText('--:--');
      return undefined;
    }
    const audio = new Audio(url);
    audio.preload = 'metadata';

    const setFromSeconds = (secondsValue) => {
      const seconds = Math.max(0, Math.floor(secondsValue || 0));
      const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
      const ss = String(seconds % 60).padStart(2, '0');
      setDurationText(`${mm}:${ss}`);
    };

    const trySetDuration = () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        setFromSeconds(audio.duration);
        return;
      }

      const originalTime = audio.currentTime || 0;
      const restore = () => {
        if (Number.isFinite(audio.duration) && audio.duration > 0) {
          setFromSeconds(audio.duration);
        }
        audio.currentTime = originalTime;
        audio.removeEventListener('timeupdate', restore);
      };

      audio.currentTime = 1e101;
      audio.addEventListener('timeupdate', restore);
    };

    audio.addEventListener('loadedmetadata', trySetDuration);
    audio.addEventListener('durationchange', trySetDuration);
    audio.load();

    return () => {
      audio.removeEventListener('loadedmetadata', trySetDuration);
      audio.removeEventListener('durationchange', trySetDuration);
      audio.src = '';
    };
  }, [url]);

  return (
    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.15 }}>
      {durationText}
    </Typography>
  );
}

function TypingIndicator({ label, color = 'text.primary' }) {
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.9,
        whiteSpace: 'nowrap',
      }}
    >
      <Typography variant="caption" sx={{ color }}>
        {label}
      </Typography>
      <Box sx={{ display: 'inline-flex', gap: 0.4, alignItems: 'center' }}>
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            sx={{
              width: 5,
              height: 5,
              borderRadius: '50%',
              bgcolor: color,
              opacity: 0.35,
              animation: 'typingDots 1.1s infinite ease-in-out',
              animationDelay: `${i * 0.18}s`,
              '@keyframes typingDots': {
                '0%, 80%, 100%': { transform: 'translateY(0)', opacity: 0.3 },
                '40%': { transform: 'translateY(-3px)', opacity: 1 },
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

function ChatFooter({
  mensagemSuporte,
  encerrarChat,
  userChat,
  chatId,
  replyTarget = null,
  onCancelReply = () => {},
}) {
  const theme = useTheme();
  const fileInputRef = useRef(null);

  const { cliente } = useAuthContext();
  const {
    handleSendMessageWS,
    clientIsTyping,
    clientIsRecording,
    audioDisabledChats,
  } = useWSContext();

  const [estaDigitando, setEstaDigitando] = useState('none');
  const [mensagem, setMensagem] = useState('');
  const [textoEncerra] = useState('Escreva sua mensagem...');
  const [cor, setCor] = useState(theme.palette.dark.default);
  const [loadingUpload, setLoadingUpload] = useState(0);
  const [alerta, setAlerta] = useState('none');
  const [alertaMensagem, setAlertaMensagem] = useState('Problema ao enviar o arquivo');
  const [barraArquivo, setBarraArquivo] = useState('none');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioStreamRef = useRef(null);
  const selectedMimeTypeRef = useRef('audio/webm');
  const shouldDiscardRecordingRef = useRef(false);
  const recordingTimerRef = useRef(null);
  const recordingElapsedRef = useRef(0);
  const autoStopTriggeredRef = useRef(false);
  const pararGravacaoAudioRef = useRef(null);
  const previewRef = useRef('');

  const hasTypedMessage = mensagem.trim().length > 0;
  const MAX_RECORDING_SECONDS = 90;
  const date = new Date();

  const resolvedChatId =
    chatId ||
    userChat?.state?.id ||
    userChat?.id ||
    userChat?.state?.chatId ||
    userChat?.state?.idCliente;

  const hasAudioDisabledMarkerInHistory = useMemo(() => {
    if (!resolvedChatId) return false;
    try {
      const history = JSON.parse(localStorage.getItem(String(resolvedChatId)) || '[]');
      return history.some((item) => {
        const text =
          item?.mensagem ||
          item?.mensage ||
          item?.message?.mensagem ||
          item?.message?.message ||
          item?.message ||
          item?.value ||
          '';
        return typeof text === 'string' && text.startsWith('[AUDIO_DISABLED_DEVICE]');
      });
    } catch (error) {
      return false;
    }
  }, [resolvedChatId]);

  const isAudioDisabledForClient =
    audioDisabledChats?.has?.(String(resolvedChatId)) || hasAudioDisabledMarkerInHistory;

  const normalizeReplyPayload = (msg) => {
    if (!msg) return null;
    const guessedType =
      msg.tipoArquivo ||
      msg.msgFileType ||
      (msg.filePath ? `${msg.filePath}`.split('.').pop()?.toLowerCase() : '');

    return {
      id: msg.id,
      userType: msg.usuario === 'suporte' ? 'suporte' : 'cliente',
      msgType: msg.serviceResp === 'receiveFile' || msg.tipoArquivo ? 'file' : 'txt',
      msgText: msg.mensage || msg.msgText || '',
      urlFile: msg.filePath || msg.urlFile || null,
      msgFileType: guessedType || null,
    };
  };

  const getMediaReplyLabel = (msg) => {
    const rawType =
      (msg?.msgFileType || `${msg?.urlFile || ''}`.split('.').pop() || '').toLowerCase();
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(rawType)) return 'Foto';
    if (['m4a', 'mp3', 'wav', 'aac', 'ogg', 'webm'].includes(rawType)) return 'Audio';
    if (['mp4', 'mov', '3gpp', 'mkv'].includes(rawType)) return 'Video';
    if (rawType === 'pdf') return 'Documento PDF';
    return 'Arquivo';
  };

  const resolveReplyMedia = (msg) => {
    const fileType =
      (msg?.msgFileType || `${msg?.urlFile || ''}`.split('.').pop() || '').toLowerCase();
    let url = msg?.urlFile || '';
    if (url && !url.startsWith('http') && process.env.REACT_APP_URL_ARQUIVOS) {
      url = `${process.env.REACT_APP_URL_ARQUIVOS}/${url}`;
    }
    return { fileType, url };
  };

  const getReplyPreview = (msg) => {
    if (!msg) return '';
    if (msg.msgType === 'file') return getMediaReplyLabel(msg);
    return msg.msgText || 'Mensagem';
  };

  const renderReplyPreview = (msg) => {
    if (!msg) return null;
    if (msg.msgType !== 'file') {
      return <Typography variant="body2">{msg.msgText || 'Mensagem'}</Typography>;
    }

    const { fileType, url } = resolveReplyMedia(msg);
    const label = getReplyPreview(msg);
    const commonThumbSx = {
      width: 44,
      height: 44,
      borderRadius: 1,
      objectFit: 'cover',
      border: '1px solid rgba(0,0,0,0.12)',
      bgcolor: 'background.paper',
    };

    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(fileType) && url) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box component="img" src={url} alt="midia" sx={commonThumbSx} />
          <Typography variant="body2">{label}</Typography>
        </Box>
      );
    }
    if (['mp4', 'mov', '3gpp', 'mkv'].includes(fileType) && url) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box component="video" src={url} muted sx={commonThumbSx} />
          <Typography variant="body2">{label}</Typography>
        </Box>
      );
    }
    if (['m4a', 'mp3', 'wav', 'aac', 'ogg', 'webm'].includes(fileType)) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GraphicEqIcon fontSize="small" />
          <Box>
            <Typography variant="body2">{label}</Typography>
            <AudioReplyInfo url={url} />
          </Box>
        </Box>
      );
    }
    if (fileType === 'pdf') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PictureAsPdfIcon fontSize="small" />
          <Typography variant="body2">{label}</Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <InsertDriveFileIcon fontSize="small" />
        <Typography variant="body2">{label}</Typography>
      </Box>
    );
  };

  const formatRecordingTime = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  useEffect(() => {
    if (encerrarChat) {
      setCor(theme.palette.dark.disabled);
    }
  }, [encerrarChat, theme.palette.dark.disabled]);

  useEffect(() => {
    if (clientIsTyping?.id === userChat?.state?.id) {
      setEstaDigitando(isWsTruthy(clientIsTyping.typing) ? 'block' : 'none');
      return;
    }
    setEstaDigitando('none');
  }, [clientIsTyping, userChat?.state?.id]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Enter' || e.shiftKey) return;
      if (isRecording) return;
      e.preventDefault();
      if (hasTypedMessage) {
        const button = document.getElementById('submit');
        button?.click();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [hasTypedMessage, isRecording]);

  useEffect(
    () => () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        shouldDiscardRecordingRef.current = true;
        handleSendMessageWS({
          serviceType: 'recordingStatus',
          id: userChat?.state?.id,
          recording: 'False',
        });
        mediaRecorderRef.current.stop();
      }
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      recordingElapsedRef.current = 0;
      autoStopTriggeredRef.current = false;
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    },
    [handleSendMessageWS, userChat?.state?.id]
  );

  const showError = (msg) => {
    setAlertaMensagem(msg || 'Problema ao enviar o arquivo');
    setAlerta('flex');
    setTimeout(() => setAlerta('none'), 8000);
  };

  const enviarMensagem = () => {
    if (mensagem.trim() === '') return;

    const replyTo = normalizeReplyPayload(replyTarget);
    const bodyMessage = {
      serviceType: 'mensage',
      id: userChat.state.id,
      name: cliente?.nome,
      mensage: mensagem,
      replyTo,
      data: format(date, 'dd-MM-yyyy HH:mm:ss'),
      row: 0,
      usuario: 'suporte',
    };
    mensagemSuporte(bodyMessage);
    setMensagem('');
    onCancelReply();
  };

  const digitandoMensagem = (value) => {
    setMensagem(value);
    const typingStatus = value !== '' ? 'True' : 'False';
    handleSendMessageWS({
      serviceType: 'typingStatus',
      id: userChat.state.id,
      name: cliente?.nome,
      typing: typingStatus,
    });
  };

  const filterTypeArchive = (nomeArquivo) => `${nomeArquivo.slice(nomeArquivo.lastIndexOf('.') + 1)}`;

  const enviarArquivo = (nomeArquivo, replyToOverride = null) => {
    const tipoArquivo = filterTypeArchive(nomeArquivo);
    const nomeFormatado = cliente?.nome ? cliente.nome.replace(/\s/g, '') : '';

    const dadosMensagem = {
      serviceType: 'sendFile',
      idCliente: userChat?.state?.id,
      filePath: `${nomeFormatado}/${nomeArquivo}`,
      replyTo: replyToOverride || normalizeReplyPayload(replyTarget),
      previewPath: previewRef.current,
      data: format(date, 'dd-MM-yyyy HH:mm:ss'),
      usuario: 'suporte',
      tipoArquivo,
    };
    mensagemSuporte(dadosMensagem);
    onCancelReply();
  };

  const getArquivo = (file, replyToOverride = null) => {
    if (
      isAudioDisabledForClient &&
      (file?.type?.startsWith('audio/') || file?.type?.startsWith('video/'))
    ) {
      showError('Cliente sem suporte de audio/video neste dispositivo. Envie texto, foto ou PDF.');
      return;
    }

    if (!cliente?.nome) {
      showError('Nome do usuario nao encontrado para upload.');
      return;
    }

    const nomeFormatado = cliente.nome.replace(/\s/g, '');
    const urlArquivo = URL.createObjectURL(file);
    previewRef.current = urlArquivo;

    const dataForm = new FormData();
    dataForm.append('file', file);

    serverArquivo
      .post(`/save/${nomeFormatado}`, dataForm, {
        onUploadProgress: (event) => {
          const progress = Math.round((event.loaded * 100) / event.total);
          setLoadingUpload(progress);
          setBarraArquivo('flex');
        },
      })
      .then((response) => {
        enviarArquivo(response.data, replyToOverride || normalizeReplyPayload(replyTarget));
        setTimeout(() => setBarraArquivo('none'), 1000);
      })
      .catch((err) => {
        console.error(`Houve um problema ao realizar o upload do arquivo ${file.name}`, err);
        setTimeout(() => setBarraArquivo('none'), 1000);
        showError('Problema ao enviar o arquivo');
      });
  };

  const iniciarGravacaoAudio = async () => {
    if (encerrarChat || isRecording) return;

    if (isAudioDisabledForClient) {
      showError('Cliente sem suporte de audio/video neste dispositivo. Envie texto, foto ou PDF.');
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      showError('Gravacao de audio indisponivel neste navegador.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      audioChunksRef.current = [];

      const preferredMimeTypes = [
        'audio/mp4',
        'audio/x-m4a',
        'audio/mpeg',
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/ogg',
      ];
      const selectedMimeType = preferredMimeTypes.find((type) => MediaRecorder.isTypeSupported(type));
      const recorder = selectedMimeType
        ? new MediaRecorder(stream, { mimeType: selectedMimeType, audioBitsPerSecond: 128000 })
        : new MediaRecorder(stream);

      handleSendMessageWS({ serviceType: 'typingStatus', id: userChat.state.id, name: cliente?.nome, typing: 'False' });

      mediaRecorderRef.current = recorder;
      selectedMimeTypeRef.current = selectedMimeType || recorder.mimeType || 'audio/webm';
      shouldDiscardRecordingRef.current = false;
      recordingElapsedRef.current = 0;
      autoStopTriggeredRef.current = false;
      setRecordingSeconds(0);

      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = setInterval(() => {
        recordingElapsedRef.current += 1;
        const next = recordingElapsedRef.current;
        setRecordingSeconds(next);

        if (next >= MAX_RECORDING_SECONDS && !autoStopTriggeredRef.current) {
          autoStopTriggeredRef.current = true;
          clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
          setTimeout(() => {
            pararGravacaoAudioRef.current?.();
          }, 0);
          showError('Tempo máximo de 1:30 atingido. Áudio finalizado.');
        }
      }, 1000);

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        try {
          if (shouldDiscardRecordingRef.current) return;
          if (audioChunksRef.current.length === 0) throw new Error('Gravacao vazia');

          const mimeType = selectedMimeTypeRef.current || recorder.mimeType || 'audio/webm';
          let extension = 'webm';
          if (mimeType.includes('ogg')) extension = 'ogg';
          if (mimeType.includes('mp4')) extension = 'm4a';
          if (mimeType.includes('mpeg')) extension = 'mp3';

          const blob = new Blob(audioChunksRef.current, { type: mimeType });
          const fileName = `audio_${Date.now()}.${extension}`;
          const audioFile = new File([blob], fileName, { type: mimeType });
          getArquivo(audioFile, normalizeReplyPayload(replyTarget));
        } finally {
          handleSendMessageWS({ serviceType: 'recordingStatus', id: userChat.state.id, recording: 'False' });
          if (audioStreamRef.current) {
            audioStreamRef.current.getTracks().forEach((track) => track.stop());
            audioStreamRef.current = null;
          }
          mediaRecorderRef.current = null;
          audioChunksRef.current = [];
          shouldDiscardRecordingRef.current = false;
          if (recordingTimerRef.current) {
            clearInterval(recordingTimerRef.current);
            recordingTimerRef.current = null;
          }
          recordingElapsedRef.current = 0;
          autoStopTriggeredRef.current = false;
          setRecordingSeconds(0);
        }
      };

      recorder.start(250);
      handleSendMessageWS({ serviceType: 'recordingStatus', id: userChat.state.id, recording: 'True' });
      setIsRecording(true);
    } catch (error) {
      console.error('Erro ao iniciar gravacao de audio:', error);
      recordingElapsedRef.current = 0;
      autoStopTriggeredRef.current = false;
      showError('Nao foi possivel gravar audio.');
    }
  };

  function pararGravacaoAudio() {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;
    handleSendMessageWS({ serviceType: 'recordingStatus', id: userChat.state.id, recording: 'False' });
    recorder.stop();
    recordingElapsedRef.current = 0;
    autoStopTriggeredRef.current = false;
    setIsRecording(false);
  }

  function cancelarGravacaoAudio() {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === 'inactive') return;
    shouldDiscardRecordingRef.current = true;
    handleSendMessageWS({ serviceType: 'recordingStatus', id: userChat.state.id, recording: 'False' });
    recorder.stop();
    recordingElapsedRef.current = 0;
    autoStopTriggeredRef.current = false;
    setIsRecording(false);
  }
  pararGravacaoAudioRef.current = pararGravacaoAudio;

  const getMainActionIcon = () => {
    if (isRecording) return <StopIcon />;
    if (isAudioDisabledForClient) return <Send />;
    if (hasTypedMessage) return <Send />;
    return <MicIcon />;
  };

  return (
    <Box>
      <Box display={alerta}>
        <Alert variant="filled" severity="error" sx={{ width: '32%', ml: 'auto', mr: 'auto' }}>
          {alertaMensagem}
        </Alert>
      </Box>

      <Box display={barraArquivo}>
        <Box position="absolute" sx={{ width: '100%', mr: 1 }}>
          <LinearProgress sx={{ height: '20px' }} variant="determinate" value={loadingUpload} />
        </Box>
        <Box position="relative" sx={{ minWidth: 60, ml: '570px' }}>
          <Typography variant="body2" color="text.secondary">{`${Math.round(loadingUpload)}%`}</Typography>
        </Box>
      </Box>

      {replyTarget && (
        <Box
          sx={{
            mx: 2,
            mb: 1,
            p: 1,
            borderRadius: 1,
            border: '1px solid rgba(255,255,255,0.35)',
            borderLeft: '3px solid rgba(255,255,255,0.75)',
            bgcolor: 'action.hover',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Respondendo
            </Typography>
            {renderReplyPreview(normalizeReplyPayload(replyTarget))}
          </Box>
          <Button size="small" onClick={onCancelReply} sx={{ color: '#fff' }}>
            Cancelar
          </Button>
        </Box>
      )}

      <Box display={estaDigitando} sx={{ float: 'right', mr: 2 }}>
        <TypingIndicator
          label={userChat?.state?.name ? `${userChat.state.name} está digitando` : 'Digitando'}
          color={theme.palette.text.primary}
        />
      </Box>

      <Box
        display={
          clientIsRecording?.id === userChat?.state?.id
          && isWsTruthy(clientIsRecording?.recording)
            ? 'block'
            : 'none'
        }
        sx={{ float: 'right', mr: 2 }}
      >
        <TypingIndicator
          label={userChat?.state?.name ? `${userChat.state.name} está gravando áudio` : 'Gravando áudio'}
          color={theme.palette.text.primary}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          width: '100%',
          padding: 2,
          flexDirection: 'row',
          alignItems: 'center',
          boxShadow: theme.shadows[10],
        }}
      >
        <Box sx={{ display: 'flex', width: '100%', flexDirection: 'row', alignItems: 'center' }}>
          <input
            type="file"
            accept={
              isAudioDisabledForClient
                ? 'image/png, image/pjg, image/jpeg, application/pdf'
                : 'image/png, image/pjg, image/jpeg,video/mp4, video/3gpp, video/mov, application/pdf,audio/webm,audio/ogg,audio/mpeg,audio/mp4,audio/wav,audio/x-m4a'
            }
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files[0];
              if (!file) return;
              getArquivo(file, normalizeReplyPayload(replyTarget));
            }}
            disabled={encerrarChat}
          />

          {!isRecording && (
            <IconButton onClick={() => fileInputRef.current?.click()} color="primary" disabled={encerrarChat}>
              <AttachFile />
            </IconButton>
          )}

          {isRecording && (
            // eslint-disable-next-line react/jsx-no-bind
            <IconButton onClick={cancelarGravacaoAudio} color="error" disabled={encerrarChat}>
              <DeleteOutlineIcon />
            </IconButton>
          )}

          <Input
            type="input"
            value={isRecording ? `Gravando • ${formatRecordingTime(recordingSeconds)}` : mensagem}
            onChange={(e) => {
              if (isRecording) return;
              digitandoMensagem(e.target.value);
            }}
            placeholder={encerrarChat ? 'CHAT ENCERRADO!' : textoEncerra}
            autoFocus
            disabled={encerrarChat || isRecording}
            id="campoMensagem"
            fullWidth
            multiline
            maxRows={4}
            sx={{
              marginLeft: 2,
              bgcolor: cor,
              borderRadius: '5px 0px 3px 3px',
              padding: 1,
              '& .MuiInputBase-input': {
                color: isRecording ? 'rgba(255,255,255,0.92)' : 'inherit',
                fontSize: isRecording ? '1.15rem' : '1rem',
                fontWeight: 400,
                textAlign: isRecording ? 'center' : 'left',
                animation: isRecording ? 'recordBlink 1.4s ease-in-out infinite' : 'none',
                '@keyframes recordBlink': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.42 },
                },
              },
            }}
          />

          <Button
            type="submit"
            id="submit"
            disabled={encerrarChat}
            onClick={() => {
              if (isRecording) {
                pararGravacaoAudio();
                return;
              }

              if (isAudioDisabledForClient) {
                if (hasTypedMessage) enviarMensagem();
                return;
              }

              if (hasTypedMessage) {
                enviarMensagem();
                return;
              }

              iniciarGravacaoAudio();
            }}
            variant="contained"
            sx={{ borderRadius: '0px 5px 5px 0px', padding: 1 }}
          >
            {getMainActionIcon()}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default ChatFooter;
